import React from 'react';
import { Card, Table, Tag, Space, Button, Modal, message, Tabs } from 'antd';
import { CheckOutlined, CloseOutlined, EyeOutlined } from '@ant-design/icons';

import { linkRequestsAPI } from '../services/api';
import { useAuthStore } from '../store/authStore';
import { useIsMobile } from '../hooks/useIsMobile';

const OrgLinkRequests: React.FC = () => {
  const [loading, setLoading] = React.useState(false);
  const [requests, setRequests] = React.useState<any[]>([]);
  const [tab, setTab] = React.useState<'pending' | 'approved' | 'rejected'>('pending');
  const { user } = useAuthStore();
  const isMobile = useIsMobile();

  React.useEffect(() => {
    load();
  }, []);

  const load = async () => {
    if (!user || (user.role !== 'supplier' && user.role !== 'admin')) {
      message.error('Только поставщики могут видеть заявки');
      return;
    }

    setLoading(true);
    try {
      const data = await linkRequestsAPI.getSupplierRequests();
      setRequests(data);
    } catch (e: any) {
      message.error(e?.response?.data?.error || 'Ошибка загрузки заявок');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (id: number) => {
    Modal.confirm({
      title: 'Подтвердить заявку',
      content: 'Вы уверены, что хотите одобрить эту заявку?',
      okText: 'Да',
      cancelText: 'Нет',
      onOk: async () => {
        try {
          await linkRequestsAPI.approveRequest(id);
          message.success('Заявка одобрена');
          load();
        } catch (e: any) {
          message.error(e?.response?.data?.error || 'Ошибка');
        }
      },
    });
  };

  const handleReject = async (id: number) => {
    Modal.confirm({
      title: 'Отклонить заявку',
      content: 'Вы уверены, что хотите отклонить эту заявку?',
      okText: 'Да',
      cancelText: 'Нет',
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await linkRequestsAPI.rejectRequest(id);
          message.success('Заявка отклонена');
          load();
        } catch (e: any) {
          message.error(e?.response?.data?.error || 'Ошибка');
        }
      },
    });
  };

  const getStatusTag = (status: string) => {
    const colors: { [key: string]: string } = {
      pending: 'orange',
      approved: 'green',
      rejected: 'red'
    };
    const labels: { [key: string]: string } = {
      pending: 'Ожидает',
      approved: 'Одобрена',
      rejected: 'Отклонена'
    };
    return <Tag color={colors[status]}>{labels[status]}</Tag>;
  };

  const filteredRequests = requests.filter((r) => r.status === tab);

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      width: 70
    },
    {
      title: 'Заказчик (ID)',
      key: 'customerRef',
      width: 100,
      render: (_: unknown, record: any) => record.customerId ?? record.customer?.id ?? '—'
    },
    {
      title: 'Организация',
      key: 'companyName',
      ellipsis: true,
      render: (_: unknown, record: any) => {
        const u = record.customer?.user;
        return u?.companyName || u?.fullName || u?.username || '—';
      }
    },
    {
      title: 'ИНН',
      key: 'inn',
      width: 130,
      render: (_: unknown, record: any) => record.customer?.user?.inn ?? '—'
    },
    {
      title: 'Контактное лицо',
      key: 'contact',
      ellipsis: true,
      render: (_: unknown, record: any) => {
        const u = record.customer?.user;
        return u?.fullName || u?.username || u?.email || '—';
      }
    },
    {
      title: 'Статус',
      dataIndex: 'status',
      render: (status: string) => getStatusTag(status),
      width: 120
    },
    {
      title: 'Дата',
      dataIndex: 'requestedAt',
      render: (date: string) => new Date(date).toLocaleDateString('ru'),
      width: 120
    },
    {
      title: 'Действия',
      render: (_: any, record: any) => (
        <Space size="small">
          {record.status === 'pending' && (
            <>
              <Button
                type="primary"
                size="small"
                icon={<CheckOutlined />}
                onClick={() => handleApprove(record.id)}
              >
                Одобрить
              </Button>
              <Button
                danger
                size="small"
                icon={<CloseOutlined />}
                onClick={() => handleReject(record.id)}
              >
                Отклонить
              </Button>
            </>
          )}
          {record.status !== 'pending' && (
            <Button size="small" icon={<EyeOutlined />}>
              Просмотр
            </Button>
          )}
        </Space>
      )
    }
  ];

  return (
    
      <Card
        title="Заявки от заказчиков"
        loading={loading}
        extra={
          <Button onClick={load}>Обновить</Button>
        }
      >
        <Tabs
          activeKey={tab}
          onChange={(k) => setTab(k as any)}
          items={[
            {
              key: 'pending',
              label: `Ожидающие (${requests.filter((r) => r.status === 'pending').length})`,
              children: (
                <Table
                  rowKey="id"
                  columns={columns as any}
                  dataSource={filteredRequests}
                  pagination={false}
                  size={isMobile ? 'small' : 'middle'}
                  scroll={{ x: isMobile ? 'max-content' : undefined }}
                  locale={{ emptyText: 'Нет заявок' }}
                />
              )
            },
            {
              key: 'approved',
              label: `Одобренные (${requests.filter((r) => r.status === 'approved').length})`,
              children: (
                <Table
                  rowKey="id"
                  columns={columns as any}
                  dataSource={filteredRequests}
                  pagination={false}
                  size={isMobile ? 'small' : 'middle'}
                  scroll={{ x: isMobile ? 'max-content' : undefined }}
                  locale={{ emptyText: 'Нет заявок' }}
                />
              )
            },
            {
              key: 'rejected',
              label: `Отклоненные (${requests.filter((r) => r.status === 'rejected').length})`,
              children: (
                <Table
                  rowKey="id"
                  columns={columns as any}
                  dataSource={filteredRequests}
                  pagination={false}
                  size={isMobile ? 'small' : 'middle'}
                  scroll={{ x: isMobile ? 'max-content' : undefined }}
                  locale={{ emptyText: 'Нет заявок' }}
                />
              )
            }
          ]}
        />
      </Card>
    
  );
};

export default OrgLinkRequests;
